import { Helmet } from "react-helmet-async";
import { FIRM } from "@/data/firm";

const SITE_URL = "https://shelton.law";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`; // placeholder — replace when a proper OG image is designed

// Reusable SEO block. Pass at least `title`; everything else falls back to sane defaults.
export default function SEO({
  title,
  description,
  path = "",
  ogImage,
  ogType = "website",
  jsonLd,
  noindex = false,
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title ? `${title} — ${FIRM.name}` : `${FIRM.name} — Counsel for Businesses and Federal Regulatory Matters`;
  const desc =
    description ||
    "Shelton Law PLLC advises closely-held companies, founders, and international clients on business transactions, intellectual property, artificial intelligence, and the federal regulatory practices that increasingly bear on them.";
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={FIRM.longName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// LegalService / Attorney schema used on Home + About
export function firmJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    name: FIRM.longName,
    url: SITE_URL,
    email: FIRM.email,
    areaServed: "United States",
    address: {
      "@type": "PostalAddress",
      addressRegion: "TN",
      addressCountry: "US",
    },
    founder: {
      "@type": "Person",
      name: FIRM.attorney,
      jobTitle: "Founding Attorney",
    },
    knowsAbout: [
      "Business Transactions",
      "International Trade",
      "Export Compliance",
      "CFIUS",
      "Government Contracts",
      "Intellectual Property",
      "Artificial Intelligence",
      "Semiconductor Industry",
      "FCC",
      "Insurance",
    ],
  };
}

export function attorneyJsonLd(attorney) {
  return {
    "@context": "https://schema.org",
    "@type": "Attorney",
    name: attorney.name,
    jobTitle: attorney.role,
    image: attorney.photo,
    worksFor: { "@type": "LegalService", name: FIRM.longName, url: SITE_URL },
    knowsAbout: attorney.practices,
    memberOf: attorney.admissions?.map((a) => ({ "@type": "Organization", name: `Bar of ${a}` })),
  };
}

export { SITE_URL };
